import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi, STEG, type Ansatt, type ReiseDetaljer } from '../../api/konsoll';
import { datoTid } from './ReiseListe';

interface Props {
  reiseId: string;
  tid: ReiseDetaljer['tid'];
  ansatt: Ansatt;
  onEndret: () => void;
}

/** Tiden per steg: tidtakeren til den ansatte, føring for hånd, og alle føringene. Summen er timer til go-live. */
export default function TidPanel({ reiseId, tid, ansatt, onEndret }: Props) {
  const { t, i18n } = useTranslation();
  const [steg, setSteg] = useState('gjennomgang');
  const [aktivitet, setAktivitet] = useState('');
  const [manuell, setManuell] = useState({ steg: 'behovsmote', aktivitet: '', minutter: '' });
  const [feil, setFeil] = useState<string | null>(null);
  const min = tid.pagaende.find((p) => p.person === ansatt.epost);

  async function utfor(handling: () => Promise<unknown>) {
    setFeil(null);
    try {
      await handling();
      onEndret();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    }
  }

  function start(event: FormEvent) {
    event.preventDefault();
    utfor(async () => {
      await konsollApi.startTid(reiseId, steg, aktivitet.trim());
      setAktivitet('');
    });
  }

  function forTid(event: FormEvent) {
    event.preventDefault();
    utfor(async () => {
      await konsollApi.forTid(reiseId, manuell.steg, manuell.aktivitet.trim(), Number(manuell.minutter));
      setManuell({ ...manuell, aktivitet: '', minutter: '' });
    });
  }

  const stegnavn = (s: string) => t(`konsoll.steg.${s}`, { defaultValue: s });

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="tid-sum">
        <h3 id="tid-sum" className="mb-2 font-semibold">
          {t('konsoll.tid.totalt', { minutter: tid.totalt })}
        </h3>
        <ul className="flex flex-wrap gap-2">
          {Object.entries(tid.perSteg).map(([s, m]) => (
            <li key={s} className="merke py-1 text-sm">
              {stegnavn(s)}: {t('konsoll.tid.minutterVerdi', { minutter: m })}
            </li>
          ))}
        </ul>
      </section>

      {feil && (
        <p role="alert" className="melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}

      <section className="kort p-5">
        {min ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p role="status">{t('konsoll.tid.gaar', { steg: stegnavn(min.steg), aktivitet: min.aktivitet })}</p>
            <button type="button" onClick={() => utfor(() => konsollApi.stoppTid(reiseId))} className="knapp-primaer">
              {t('konsoll.tid.stopp')}
            </button>
          </div>
        ) : (
          <form onSubmit={start} className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
            <Stegvelger id="start-steg" verdi={steg} onEndre={setSteg} />
            <div className="flex flex-col">
              <label htmlFor="start-aktivitet" className="mb-1 text-sm font-medium">
                {t('konsoll.tid.aktivitet')}
              </label>
              <input id="start-aktivitet" value={aktivitet} onChange={(e) => setAktivitet(e.target.value)} className="felt" />
            </div>
            <button type="submit" className="knapp-primaer">
              {t('konsoll.tid.start')}
            </button>
          </form>
        )}
      </section>

      <section aria-labelledby="tid-manuell" className="kort p-5">
        <h3 id="tid-manuell" className="mb-3 font-semibold">
          {t('konsoll.tid.manuell')}
        </h3>
        <form onSubmit={forTid} className="grid gap-3 sm:grid-cols-[auto_1fr_8rem_auto] sm:items-end">
          <Stegvelger id="manuell-steg" verdi={manuell.steg} onEndre={(s) => setManuell({ ...manuell, steg: s })} />
          <div className="flex flex-col">
            <label htmlFor="manuell-aktivitet" className="mb-1 text-sm font-medium">
              {t('konsoll.tid.aktivitet')}
            </label>
            <input id="manuell-aktivitet" value={manuell.aktivitet} onChange={(e) => setManuell({ ...manuell, aktivitet: e.target.value })} className="felt" />
          </div>
          <div className="flex flex-col">
            <label htmlFor="manuell-minutter" className="mb-1 text-sm font-medium">
              {t('konsoll.tid.minutter')}
            </label>
            <input
              id="manuell-minutter"
              inputMode="numeric"
              value={manuell.minutter}
              onChange={(e) => setManuell({ ...manuell, minutter: e.target.value.replace(/\D/g, '') })}
              className="felt"
            />
          </div>
          <button type="submit" className="knapp-sekundaer">
            {t('konsoll.tid.forTid')}
          </button>
        </form>
      </section>

      <section aria-labelledby="tid-foringer">
        <h3 id="tid-foringer" className="mb-2 font-semibold">
          {t('konsoll.tid.foringer')}
        </h3>
        {tid.foringer.length === 0 && <p className="text-slate-700">{t('konsoll.tid.ingen')}</p>}
        <ul className="flex flex-col gap-1">
          {tid.foringer.map((f) => (
            <li key={f.id} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm">
              {stegnavn(f.steg)} · {f.aktivitet} · {t('konsoll.tid.minutterVerdi', { minutter: f.minutter })} · {f.person} ·{' '}
              {datoTid(f.tidspunkt, i18n.resolvedLanguage)}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stegvelger({ id, verdi, onEndre }: { id: string; verdi: string; onEndre: (steg: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="mb-1 text-sm font-medium">
        {t('konsoll.tid.steg')}
      </label>
      <select id={id} value={verdi} onChange={(e) => onEndre(e.target.value)} className="felt">
        {STEG.map((s) => (
          <option key={s} value={s}>
            {t(`konsoll.steg.${s}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
